<template>
  <div class="pontaj-page">
    <div class="pontaj-inner">
      <!-- Hero -->
      <div class="hero">
        <div class="hero-txt">
          <div class="hero-eyebrow">PORTAL HR</div>
          <h1 class="hero-title">Pontaj</h1>
          <p class="hero-sub">Situația orelor lucrate pentru luna selectată.</p>
        </div>
        <div class="hero-actions">
          <div class="stat-chip">
            <v-icon size="18" class="mr-1">mdi-clock-check-outline</v-icon>
            <span class="stat-val">{{ totalOreText }}</span>
            <span class="stat-lbl">total ore</span>
          </div>
          <div class="stat-chip">
            <v-icon size="18" class="mr-1">mdi-calendar-check-outline</v-icon>
            <span class="stat-val">{{ zileLucrate }}</span>
            <span class="stat-lbl">zile lucrate</span>
          </div>
          <div class="stat-chip">
            <v-icon size="18" class="mr-1">mdi-chart-line</v-icon>
            <span class="stat-val">{{ medieZi }}</span>
            <span class="stat-lbl">medie / zi</span>
          </div>
        </div>
      </div>

      <!-- Filtre -->
      <div class="filter-bar">
        <v-select
          v-model="luna"
          :items="luni"
          item-title="text"
          item-value="val"
          label="Luna"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width:180px"
          @update:model-value="incarca"
        />
        <v-select
          v-model="an"
          :items="ani"
          label="An"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width:120px"
          @update:model-value="incarca"
        />
      </div>

      <v-progress-linear v-if="loading" indeterminate color="#ff5722" class="mb-3" rounded height="3" />

      <!-- Calendar lunar -->
      <div class="cal-card">
        <div class="cal-head">
          <v-icon size="18" color="#ff5722" class="mr-2">mdi-calendar-month-outline</v-icon>
          <span>Calendar {{ lunaText }} {{ an }}</span>
        </div>
        <div class="cal-weekdays">
          <span v-for="wd in weekdays" :key="wd">{{ wd }}</span>
        </div>
        <div class="cal-grid">
          <div
            v-for="(cell, idx) in calendarCells"
            :key="idx"
            class="cal-cell"
            :class="{ 'is-empty': !cell, 'is-weekend': cell && cell.isWeekend, 'has-hours': cell && cell.ore > 0 }"
            :style="cell && cell.ore > 0 ? { background: fillColor(cell.ore) } : {}"
          >
            <template v-if="cell">
              <span class="cell-day">{{ cell.day }}</span>
              <span class="cell-ore">{{ cell.ore > 0 ? cell.oreText : '—' }}</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Detaliu pontaje -->
      <div class="grid-card">
        <div class="grid-head">
          <v-icon size="18" color="#ff5722" class="mr-2">mdi-format-list-bulleted</v-icon>
          <span>Detaliu pontaje</span>
        </div>
        <DxDataGrid
          :data-source="zile"
          key-expr="ID"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :row-alternation-enabled="true"
          :hover-state-enabled="true"
          :no-data-text="loading ? 'Se încarcă…' : 'Nu există pontaj pentru luna selectată.'"
        >
          <DxColumn data-field="Data" caption="Data" :width="130" cell-template="dataCell" />
          <DxColumn data-field="Intrare" caption="Intrare" :width="110" />
          <DxColumn data-field="Plecare" caption="Plecare" :width="110" />
          <DxColumn data-field="OreLucrate" caption="Ore lucrate" :width="120" />

          <template #dataCell="{ data }">
            {{ fmtData(data.data.Data) }}
          </template>

          <DxPaging :enabled="false" />
        </DxDataGrid>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxDataGrid, DxColumn, DxPaging } from 'devextreme-vue/data-grid'
import PORTAL_Pontaj from '@/services/Portal/PORTAL_Pontaj'

interface ZiPontaj {
  ID: string;
  Data: string;
  Intrare: string;
  Plecare: string;
  OreDecimal: number;
  OreLucrate: string;
}

interface CalCell {
  day: number;
  isWeekend: boolean;
  ore: number;
  oreText: string;
}

export default defineComponent({
  name: 'Pontaj',
  components: { DxDataGrid, DxColumn, DxPaging },
  data() {
    const now = new Date();
    return {
      loading: false,
      zile: [] as ZiPontaj[],
      totalOre: 0,
      totalOreText: '00:00',
      zileLucrate: 0,
      luna: now.getMonth() + 1,
      an: now.getFullYear(),
      weekdays: ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'],
      luni: [
        { val: 1, text: 'Ianuarie' }, { val: 2, text: 'Februarie' }, { val: 3, text: 'Martie' },
        { val: 4, text: 'Aprilie' }, { val: 5, text: 'Mai' }, { val: 6, text: 'Iunie' },
        { val: 7, text: 'Iulie' }, { val: 8, text: 'August' }, { val: 9, text: 'Septembrie' },
        { val: 10, text: 'Octombrie' }, { val: 11, text: 'Noiembrie' }, { val: 12, text: 'Decembrie' }
      ],
      ani: [now.getFullYear() - 1, now.getFullYear()]
    }
  },
  computed: {
    lunaText(): string {
      return this.luni.find(l => l.val === this.luna)?.text ?? '';
    },
    medieZi(): string {
      if (!this.zileLucrate) return '0.0 h';
      return (this.totalOre / this.zileLucrate).toFixed(1) + ' h';
    },
    // hartă zi-a-lunii → total ore lucrate în ziua respectivă
    hoursByDay(): Record<number, number> {
      const map: Record<number, number> = {};
      for (const z of this.zile) {
        const d = new Date(z.Data);
        const day = d.getDate();
        map[day] = (map[day] ?? 0) + (z.OreDecimal ?? 0);
      }
      return map;
    },
    maxOre(): number {
      const vals = Object.values(this.hoursByDay) as number[];
      return vals.length ? Math.max(...vals, 8) : 8;
    },
    calendarCells(): (CalCell | null)[] {
      const cells: (CalCell | null)[] = [];
      const firstDay = new Date(this.an, this.luna - 1, 1);
      const daysInMonth = new Date(this.an, this.luna, 0).getDate();
      // Luni = 0 ... Duminică = 6
      const leading = (firstDay.getDay() + 6) % 7;
      for (let i = 0; i < leading; i++) cells.push(null);
      for (let day = 1; day <= daysInMonth; day++) {
        const dow = new Date(this.an, this.luna - 1, day).getDay();
        const ore = this.hoursByDay[day] ?? 0;
        cells.push({
          day,
          isWeekend: dow === 0 || dow === 6,
          ore,
          oreText: this.decimalToHHMM(ore)
        });
      }
      return cells;
    }
  },
  mounted() { this.incarca(); },
  methods: {
    async incarca() {
      this.loading = true;
      try {
        const resp = await PORTAL_Pontaj.getPontajLunar(this.an, this.luna);
        const data = resp.data ?? {};
        this.zile = data.Zile ?? [];
        this.totalOre = data.TotalOre ?? 0;
        this.totalOreText = data.TotalOreText ?? '00:00';
        this.zileLucrate = data.ZileLucrate ?? 0;
      } catch {
        this.zile = [];
        this.totalOre = 0;
        this.totalOreText = '00:00';
        this.zileLucrate = 0;
      } finally {
        this.loading = false;
      }
    },
    fmtData(d: string) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    decimalToHHMM(ore: number): string {
      if (!ore) return '';
      const h = Math.floor(ore);
      const m = Math.round((ore - h) * 60);
      return `${h}h${m ? ' ' + m + 'm' : ''}`;
    },
    fillColor(ore: number): string {
      const ratio = Math.min(1, ore / this.maxOre);
      const alpha = 0.12 + ratio * 0.55;
      return `rgba(255, 87, 34, ${alpha.toFixed(2)})`;
    }
  }
})
</script>

<style scoped>
.pontaj-page { padding: 16px; width: 100%; }
.pontaj-inner { width: 100%; }

/* Hero */
.hero {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 24px 28px; color: #fff;
  box-shadow: 0 10px 30px rgba(255, 87, 34, .28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; }
.hero-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.stat-chip {
  display: flex; align-items: center; gap: 5px; background: rgba(255, 255, 255, .22);
  padding: 9px 15px; border-radius: 22px;
}
.stat-val { font-size: 18px; font-weight: 800; }
.stat-lbl { font-size: 11px; font-weight: 600; opacity: .92; margin-left: 4px; }

/* Filtre */
.filter-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }

/* Calendar */
.cal-card { background: #fff; border: 1px solid #eef1f6; border-radius: 18px; box-shadow: 0 1px 3px rgba(15, 23, 42, .05); padding: 18px 20px; margin-bottom: 18px; }
.cal-head { display: flex; align-items: center; padding-bottom: 14px; font-size: 14px; font-weight: 700; color: #1e293b; border-bottom: 1px solid #f1f5f9; margin-bottom: 14px; }
.cal-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; margin-bottom: 8px; }
.cal-weekdays span { text-align: center; font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .4px; }
.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; }
.cal-cell {
  aspect-ratio: 1 / 0.75; min-height: 62px; border-radius: 12px; background: #f8fafc;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  transition: transform .12s;
}
.cal-cell.is-empty { background: transparent; }
.cal-cell.is-weekend { background: #f1f5f9; }
.cal-cell.has-hours:hover { transform: translateY(-2px); }
.cell-day { font-size: 12px; font-weight: 700; color: #1e293b; }
.cal-cell.has-hours .cell-day { color: #7c2d12; }
.cell-ore { font-size: 11px; font-weight: 600; color: #94a3b8; }
.cal-cell.has-hours .cell-ore { color: #ff5722; font-weight: 800; }

/* Grid detaliu */
.grid-card { background: #fff; border: 1px solid #eef1f6; border-radius: 18px; box-shadow: 0 1px 3px rgba(15, 23, 42, .05); overflow: hidden; }
.grid-head { display: flex; align-items: center; padding: 16px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; font-weight: 700; color: #1e293b; }
</style>
